// "+ Filter" button used by both the tabular template and the realtime
// list. Click → dropdown of column names → click a column → opens
// ColumnFilterMenu anchored to the button position.

import { Filter, Plus } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import type { ColumnDef } from '../types'
import ColumnFilterMenu from './ColumnFilterMenu'
import type { FilterSpec } from './gridTypes'
import { listHeadingOf } from '../utils'

interface Props {
  columns: ColumnDef[]
  columnKeyOf: (c: ColumnDef) => string
  filters: FilterSpec[]
  onUpsert: (f: FilterSpec) => void
  onRemove: (column: string) => void
}

export default function AddFilterButton({
  columns, columnKeyOf, filters, onUpsert, onRemove,
}: Props) {
  const btnRef = useRef<HTMLButtonElement>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [editing, setEditing] = useState<ColumnDef | null>(null)
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null)

  const openPicker = () => {
    if (btnRef.current) setAnchorRect(btnRef.current.getBoundingClientRect())
    setPickerOpen(true)
  }

  return (
    <>
      <button
        ref={btnRef}
        onClick={openPicker}
        className="btn btn-ghost btn-sm"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '4px 8px', fontSize: '0.8425rem',
        }}
        title="Filter the rows shown on this page"
      >
        <Filter size={11} />
        <Plus size={10} />
        <span>Filter in table</span>
      </button>

      {pickerOpen && (
        <ColumnPicker
          anchorRect={anchorRect}
          columns={columns}
          onPick={(c) => {
            setEditing(c)
            setPickerOpen(false)
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}

      {editing && (
        <ColumnFilterMenu
          column={editing}
          columnKey={columnKeyOf(editing)}
          current={filters.find(f => f.column === columnKeyOf(editing)) ?? null}
          onApply={(f) => { onUpsert(f); setEditing(null) }}
          onClear={() => onRemove(columnKeyOf(editing))}
          onClose={() => setEditing(null)}
          anchorRect={anchorRect}
        />
      )}
    </>
  )
}

function ColumnPicker({
  anchorRect, columns, onPick, onClose,
}: {
  anchorRect: DOMRect | null
  columns: ColumnDef[]
  onPick: (c: ColumnDef) => void
  onClose: () => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  // Click-outside / Esc to close.
  if (typeof window !== 'undefined') {
    // Inline listeners — set/teardown via useEffect would mean adding
    // imports; keeping this tight.
  }
  return (
    <div
      ref={ref}
      data-grid-col-picker="1"
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'fixed',
        top: anchorRect ? anchorRect.bottom + 4 : 80,
        left: anchorRect ? anchorRect.left : 80,
        zIndex: 2200,
        background: 'var(--color-bg)',
        color: 'var(--color-text)',
        border: '1px solid var(--color-border)',
        borderRadius: 8,
        boxShadow: '0 14px 40px rgba(0,0,0,0.3)',
        minWidth: 200, maxHeight: 320, overflow: 'auto',
        padding: 4, fontSize: '0.8425rem',
      }}
      // Backdrop is the parent's responsibility — for click-outside we
      // mount a transparent layer:
    >
      <CloseOnOutside onClose={onClose} />
      {columns.map((c) => (
        <button
          key={c.col_id || c.name}
          onClick={() => onPick(c)}
          style={{
            display: 'block', width: '100%', textAlign: 'left',
            padding: '6px 10px', background: 'transparent',
            border: 0, color: 'var(--color-text)', cursor: 'pointer',
            fontSize: '0.8425rem', borderRadius: 4,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-secondary)' }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
        >
          {listHeadingOf(c)}
        </button>
      ))}
      {columns.length === 0 && (
        <div style={{ padding: 10, color: 'var(--color-text-secondary)' }}>
          No filterable columns
        </div>
      )}
    </div>
  )
}

function CloseOnOutside({ onClose }: { onClose: () => void }) {
  useEffect(() => {
    // Defer the listener install by one tick so the click that opened
    // the popover doesn't immediately close it.
    let installed = false
    const t = setTimeout(() => { installed = true }, 0)
    const onDoc = (e: MouseEvent) => {
      if (!installed) return
      const target = e.target as HTMLElement
      if (target.closest('[data-grid-col-picker]')) return
      onClose()
    }
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onEsc)
    return () => {
      clearTimeout(t)
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onEsc)
    }
  }, [onClose])
  return null
}
